/* WonderBuddy - stop playback the moment it scrolls out of view.
   ----------------------------------------------------------------------
   One shared watcher for every video/audio/tracklist player on the site,
   rather than teaching each section's own player script to track scroll
   position - that would mean the same IntersectionObserver logic copied
   into wb-sbm.js, wb-dvp.js, wb-divine-audio.liquid and wb-plp-library.liquid
   separately, with four chances to drift out of sync.

   Two ways media gets registered:

   1. Automatic, for every native <audio>/<video> element anywhere on the
      page. The browser's own 'play' event does not bubble, but it still
      fires during the CAPTURE phase, so one listener on `document` with
      `capture: true` sees every element start playing, however it was
      started - a click handler in any of the section scripts, the
      `autoplay` attribute on a just-inserted reel video, all of it.

   2. Manual, via `window.wbMediaGuard.watch(el, stopFn)`, for the one
      pattern the automatic path cannot see: a detached `new Audio()` that
      is never inserted into the page (wb-plp-library.liquid's sample
      player). An event on a node with no parent never reaches a listener
      on `document`, capture phase or not - there is no path for it to
      travel. That file calls this directly, watching the pressed button
      in its place and pausing the real audio object when it goes.

   YouTube/Vimeo embeds get a third path: neither of the above can reach
   into another site's iframe, so a MutationObserver watches for iframes
   being inserted (the review/testimonial "reel" pattern swaps a poster for
   one on first press) and asks the embed to pause over postMessage - the
   same wire protocol both platforms' own players already listen for. */
(function () {
  if (window.wbMediaGuard) { return; }

  var watched = new WeakSet();
  var io = null;

  function observer() {
    if (io) { return io; }
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* threshold 0 fires the moment the element crosses from partially
           visible to fully off-screen - "scrolled away", not "scrolled a
           pixel". Nothing happens on the way back in: this stops playback,
           it does not restart it. */
        if (!entry.isIntersecting && entry.target.__wbStop) {
          entry.target.__wbStop();
        }
      });
    }, { threshold: 0 });
    return io;
  }

  function watch(el, stopFn) {
    if (!el) { return; }
    el.__wbStop = stopFn;
    if (!watched.has(el)) {
      watched.add(el);
      observer().observe(el);
    }
  }

  /* ---- 1. native <audio>/<video>, zero configuration ---------------------
     An <audio> with no `controls` is display:none - no box at all - and an
     IntersectionObserver reports a boxless element as "not intersecting"
     forever. Watching it directly therefore paused the sample the instant it
     started and never tracked scroll. So the thing watched is the first of:
     the element itself if it has a box, the button that started it
     (`wbOwner`, set by wb-sbm.js / wb-dvp.js / wb-divine.js), or its nearest
     visible ancestor. The stop only fires if that target is STILL the
     current one, so an earlier track's button scrolling away can't stop the
     track that replaced it. */
  function hasBox(node) {
    return !!(node && node.getClientRects().length &&
      (node.offsetWidth || node.offsetHeight));
  }

  function standIn(el) {
    if (hasBox(el)) { return el; }
    if (hasBox(el.wbOwner)) { return el.wbOwner; }
    var node = el.parentElement;
    while (node && !hasBox(node)) { node = node.parentElement; }
    return node;
  }

  document.addEventListener('play', function (e) {
    var el = e.target;
    if (!(el instanceof HTMLMediaElement)) { return; }
    var target = standIn(el);
    if (!target) { return; }
    el.__wbTarget = target;
    watch(target, function () {
      if (el.__wbTarget === target) { el.pause(); }
    });
  }, true);

  /* ---- no download / save-as on any audio or video -----------------------
     `controlslist="nodownload"` removes Download from Chrome/Edge's ⋮ menu;
     blocking the right-click menu removes "Save video/audio as…". Applied
     here rather than per section so it also covers reel videos inserted
     later from a <template> and Shopify's own `video_tag` output, which
     can't take the attribute. This hides the easy route - it can't stop
     someone who goes digging in the page source. */
  function noDownload(root) {
    var list = root.matches && root.matches('audio,video') ? [root] : [];
    if (root.querySelectorAll) {
      list = list.concat([].slice.call(root.querySelectorAll('audio,video')));
    }
    list.forEach(function (m) {
      if (m.controlsList) { m.controlsList.add('nodownload'); }
      else { m.setAttribute('controlslist', 'nodownload'); }
    });
  }

  document.addEventListener('contextmenu', function (e) {
    if (e.target instanceof HTMLMediaElement) { e.preventDefault(); }
  }, true);

  noDownload(document);
  document.addEventListener('DOMContentLoaded', function () { noDownload(document); });

  /* ---- 3. YouTube / Vimeo embeds ----------------------------------------
     Best-effort: this is the standard postMessage control protocol both
     platforms' own embedded players already listen for, sent without
     requiring their JS SDKs to be loaded on this page. It cannot reach an
     embed that ignores the message, but doing nothing is the same failure
     mode the page already had, not a worse one. */
  function pauseEmbed(iframe) {
    var src = iframe.src || '';
    try {
      if (/(^|\.)youtube(-nocookie)?\.com$/.test(new URL(src).hostname)) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      } else if (/(^|\.)vimeo\.com$/.test(new URL(src).hostname)) {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    } catch (err) { /* cross-origin src that fails to parse - nothing to do */ }
  }

  function scanIframe(node) {
    if (!node.src || watched.has(node)) { return; }
    if (!/youtube(-nocookie)?\.com|vimeo\.com/.test(node.src)) { return; }
    watch(node, function () { pauseEmbed(node); });
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes && m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) { return; }
        noDownload(node);
        if (node.tagName === 'IFRAME') { scanIframe(node); }
        else if (node.querySelectorAll) {
          node.querySelectorAll('iframe').forEach(scanIframe);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---- 2. manual registration for anything the above can't see ---------- */
  window.wbMediaGuard = { watch: watch };
})();
